<script setup>
import {computed, onMounted, ref} from 'vue'
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import {useI18n} from 'vue-i18n';

const {t} = useI18n();

const props = defineProps({
  accountStatus: {type: Number, required: false, default: 1},
  accountStatusReason: {type: String, required: false, default: null},
  accessDeactivationDate: {type: String, required: false, default: null},
});

// IMA-511: hodnoty odpovídají ESO kódům na backendu (App\Enums\ClientAccountStatusEnum) - drží se
// jimi, ne názvem/labelem, ten se může v budoucnu přejmenovat beze změny uložené hodnoty.
const availableStatuses = [
  {value: 1, label: t('updateClientAccountStatusModal.status_valid')},
  {value: 0, label: t('updateClientAccountStatusModal.status_unapproved')},
  {value: 5, label: t('updateClientAccountStatusModal.status_inheritance_process')},
  {value: 6, label: t('updateClientAccountStatusModal.status_inheritance_closed')},
  {value: 9, label: t('updateClientAccountStatusModal.status_invalid')},
];

const form = ref({
  account_status: 1,
  account_status_reason: '',
  access_deactivation_date: '',
});

onMounted(() => {
  form.value = {
    account_status: props.accountStatus ?? 1,
    account_status_reason: props.accountStatusReason ?? '',
    access_deactivation_date: props.accessDeactivationDate ?? '',
  };
});

const isInvalidStatus = computed(() => form.value.account_status === 9);

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', {...form.value});
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('updateClientAccountStatusModal.title') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12">
                    <label for="accountStatusInput" class="form-label">{{ t('updateClientAccountStatusModal.status') }}</label>
                    <select v-model="form.account_status" id="accountStatusInput" class="form-select" required>
                      <option v-for="status in availableStatuses" :key="status.value" :value="status.value">{{ status.label }}</option>
                    </select>
                  </div>
                </div>
                <template v-if="isInvalidStatus">
                  <div class="row mb-3">
                    <div class="col-12">
                      <label for="accountStatusReasonInput" class="form-label">{{ t('updateClientAccountStatusModal.reason') }}</label>
                      <input v-model="form.account_status_reason" id="accountStatusReasonInput" class="form-control" type="text"/>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12">
                      <label for="accessDeactivationDateInput" class="form-label">{{ t('updateClientAccountStatusModal.deactivation_date') }}</label>
                      <input v-model="form.access_deactivation_date" id="accessDeactivationDateInput" class="form-control" type="date" required/>
                      <small class="text-muted">{{ t('updateClientAccountStatusModal.deactivation_date_hint') }}</small>
                    </div>
                  </div>
                </template>
              </div>
              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('updateClientAccountStatusModal.cancel') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('updateClientAccountStatusModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
